import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  getDocumentsValidationSchema,
  type DocumentsUploadFormData,
} from "Components/Onboarding/shared/DocumentsUpload/DocumentsUploadForm/schema";
import { LayoutWithSubmitButton } from "Components/Onboarding/shared/DocumentsUpload/LayoutWithSubmitButton";
import { IdentityCardForms } from "Components/Onboarding/shared/IdentityCardForms";
import { ValuesBox } from "Components/Onboarding/shared/ValuesBox";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { BlFileInputForm } from "Components/Shared/Inputs/Form/BlFileInputForm";
import { Resources, useResource } from "Translations/Resources";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  SECOND_DOCUMENT_TYPE_OPTIONS,
  SecondDocumentTypes,
} from "Components/Onboarding/shared/DocumentsUpload/types";

const StepResources = Resources.Onboarding.Steps.Documents;

type Props = {
  onSubmit: (data: DocumentsUploadFormData) => void;
  isSecondDocumentRequired?: boolean;
};

export const DocumentsUploadForm = ({
  onSubmit,
  isSecondDocumentRequired = false,
}: Props) => {
  const { t } = useResource();
  const schema = getDocumentsValidationSchema(t, isSecondDocumentRequired);

  const { control, handleSubmit } = useForm<DocumentsUploadFormData>({
    resolver: zodResolver(schema),
  });

  const selectedDocumentType = useWatch({
    control,
    name: "secondDocument.documentType",
  });

  const isDrivingLicense =
    selectedDocumentType === SecondDocumentTypes.DrivingLicense;

  return (
    <FullHeightForm onSubmit={handleSubmit(onSubmit)}>
      <LayoutWithSubmitButton>
        <Stack gap={6}>
          <IdentityCardForms
            control={control}
            frontSideName="firstDocument.frontSide"
            backSideName="firstDocument.backSide"
          />
          {isSecondDocumentRequired && (
            <ValuesBox title={t(StepResources.SecondDocumentTitle)} hideDivider>
              <Typography>
                {t(StepResources.SecondDocumentDescription)}
              </Typography>
              <Controller
                control={control}
                name="secondDocument.documentType"
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel>
                      {t(StepResources.Forms.SecondDocumentTypeLabel)}
                    </InputLabel>
                    <Select
                      {...field}
                      value={field.value ?? ""}
                      label={t(StepResources.Forms.SecondDocumentTypeLabel)}
                    >
                      {SECOND_DOCUMENT_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
              {selectedDocumentType &&
                (isDrivingLicense ? (
                  <IdentityCardForms
                    control={control}
                    frontSideName="secondDocument.frontSide"
                    backSideName="secondDocument.backSide"
                  />
                ) : (
                  <BlFileInputForm
                    control={control}
                    label={t(StepResources.Forms.SecondDocumentFrontSide)}
                    name="secondDocument.frontSide"
                    title={t(StepResources.Forms.DocumentTitle)}
                    description={t(StepResources.Forms.DocumentDescription)}
                  />
                ))}
            </ValuesBox>
          )}
        </Stack>
      </LayoutWithSubmitButton>
    </FullHeightForm>
  );
};
